import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import Footer from './Footer';

const SolutionCard = ({ title, description, features, icon, gradient }) => (
  <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-1">
    <div className={`bg-gradient-to-br ${gradient} rounded-2xl p-8 h-full`}>
      <div className="flex flex-col h-full">
        <div className="flex items-start gap-4 mb-6">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
              {icon}
            </div>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
          </div>
        </div>
        <p className="text-gray-300 mb-6">{description}</p>
        <div className="mt-auto">
          <h4 className="text-white font-medium mb-3">Key Features:</h4>
          <ul className="space-y-2">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start gap-2">
                <svg className="w-5 h-5 text-[#FF56A5] mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-300">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </div>
);

const Solutions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const solutions = [
    {
      title: "Customer Service",
      description: "Transform your customer support with AI agents that provide instant, accurate responses 24/7.",
      features: [
        "Instant response to common inquiries",
        "Seamless escalation to human agents",
        "Multi-language support",
        "Sentiment analysis and customer satisfaction tracking"
      ],
      icon: (
        <svg className="w-6 h-6 text-[#FF56A5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      ),
      gradient: "from-[#FF56A5]/20 via-[#E852FF]/20 to-[#9F44F2]/20"
    },
    {
      title: "Sales & Lead Generation",
      description: "Automate lead qualification and nurturing while maintaining a personal touch.",
      features: [
        "Intelligent lead scoring",
        "Automated follow-ups",
        "Meeting scheduling",
        "Pipeline management integration"
      ],
      icon: (
        <svg className="w-6 h-6 text-[#E852FF]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
      gradient: "from-[#E852FF]/20 via-[#9F44F2]/20 to-[#FF56A5]/20"
    },
    {
      title: "HR & Recruitment",
      description: "Streamline your recruitment process with AI-powered candidate screening and engagement.",
      features: [
        "Automated candidate screening",
        "Interview scheduling",
        "Onboarding assistance",
        "Employee query resolution"
      ],
      icon: (
        <svg className="w-6 h-6 text-[#9F44F2]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      gradient: "from-[#9F44F2]/20 via-[#FF56A5]/20 to-[#E852FF]/20"
    },
    {
      title: "Healthcare Communication",
      description: "Enhance patient engagement and streamline administrative tasks in healthcare settings.",
      features: [
        "Appointment scheduling",
        "Medication reminders",
        "Health record updates",
        "Patient follow-up management"
      ],
      icon: (
        <svg className="w-6 h-6 text-[#FF56A5]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      ),
      gradient: "from-[#FF56A5]/20 via-[#E852FF]/20 to-[#9F44F2]/20"
    },
    {
      title: "E-commerce Support",
      description: "Provide seamless shopping assistance and order management for online retail.",
      features: [
        "Product recommendations",
        "Order tracking",
        "Return processing",
        "Inventory inquiries"
      ],
      icon: (
        <svg className="w-6 h-6 text-[#E852FF]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      gradient: "from-[#E852FF]/20 via-[#9F44F2]/20 to-[#FF56A5]/20"
    },
    {
      title: "Financial Services",
      description: "Automate financial advisory services and customer support for banking and insurance.",
      features: [
        "Account inquiries",
        "Transaction assistance",
        "Financial product guidance",
        "Fraud alert management"
      ],
      icon: (
        <svg className="w-6 h-6 text-[#9F44F2]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      gradient: "from-[#9F44F2]/20 via-[#FF56A5]/20 to-[#E852FF]/20"
    }
  ];

  return (
    <div className="landing-page bg-background min-h-screen">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Industry{' '}
            <span className="bg-gradient-to-r from-[#FF56A5] via-[#E852FF] to-[#9F44F2] bg-clip-text text-transparent">
              Solutions
            </span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Discover how Lifelike Agents can transform communication across different industries
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {solutions.map((solution, index) => (
            <SolutionCard key={index} {...solution} />
          ))}
        </div>

        {/* CTA Section */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-1">
            <div className="bg-gradient-to-br from-[#FF56A5]/20 via-[#E852FF]/20 to-[#9F44F2]/20 rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-semibold text-white mb-4">
                Ready to Transform Your Industry?
              </h2>
              <p className="text-gray-300 mb-6">
                Let's discuss how Lifelike Agents can help your business thrive.
              </p>
              <Link
                to="/request-demo"
                className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-full text-white bg-gradient-to-r from-[#FF56A5] via-[#E852FF] to-[#9F44F2] hover:from-[#FF56A5]/90 hover:via-[#E852FF]/90 hover:to-[#9F44F2]/90 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Schedule a Consultation
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Solutions; 